import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Divider,
  Stack,
  Paper,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Tooltip,
  Collapse,
  FormControlLabel,
  Grid,
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as ContentCopyIcon,
  History as HistoryIcon,
  Save as SaveIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Monitor as MonitorIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface PasswordHistory {
  id: number;
  changed_at: string;
  changed_by: string;
  change_type: 'reset' | 'update';
  old_username: string | null;
  old_password: string | null;
  password_label: string | null;
  action_details: string | null;
}

interface PasswordData {
  username: string;
  temp_password: string | null;
  password_changed: boolean;
  is_active: boolean;
  last_login: string | null;
  note_verification: string | null;
  email_verification: string | null;
  phone_verification: string | null;
  passwords: Array<{
    id: number;
    label: string;
    username: string | null;
    password: string;
    note_verification: string | null;
    email_verification: string | null;
    phone_verification: string | null;
    use_for_monitoring: boolean;
    created_at: string;
  }>;
  history: PasswordHistory[];
}

interface PasswordManagementModalProps {
  open: boolean;
  onClose: () => void;
  clientId: number;
  clientName: string;
}

const PasswordManagementModal: React.FC<PasswordManagementModalProps> = ({
  open,
  onClose,
  clientId,
  clientName,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [showPassword, setShowPassword] = useState<{ [key: string]: boolean }>({});
  const [data, setData] = useState<PasswordData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingPasswordId, setEditingPasswordId] = useState<number | null>(null);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Form fields for editing
  const [editingData, setEditingData] = useState<{
    label: string;
    username: string;
    password: string;
    note: string;
    email: string;
    phone: string;
    monitoring: boolean;
  }>({ label: '', username: '', password: '', note: '', email: '', phone: '', monitoring: false });

  // Form fields for new password
  const [newPasswordLabel, setNewPasswordLabel] = useState('');
  const [newPasswordUsername, setNewPasswordUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordNote, setNewPasswordNote] = useState('');
  const [newPasswordEmail, setNewPasswordEmail] = useState('');
  const [newPasswordPhone, setNewPasswordPhone] = useState('');
  const [newPasswordMonitoring, setNewPasswordMonitoring] = useState(false);

  useEffect(() => {
    if (open) {
      loadData();
      setActiveTab(0);
      setShowAddForm(false);
      setEditingPasswordId(null);
    }
  }, [open, clientId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/clients/${clientId}/password-management`);
      const passwordData = response.data.data;
      setData(passwordData);
    } catch (error) {
      console.error('Error loading password data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPassword = async () => {
    if (!newPasswordLabel) return;

    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/password-management/passwords`, {
        label: newPasswordLabel,
        username: newPasswordUsername,
        password: newPassword,
        note_verification: newPasswordNote,
        email_verification: newPasswordEmail,
        phone_verification: newPasswordPhone,
        use_for_monitoring: newPasswordMonitoring,
      });
      setNewPasswordLabel('');
      setNewPasswordUsername('');
      setNewPassword('');
      setNewPasswordNote('');
      setNewPasswordEmail('');
      setNewPasswordPhone('');
      setNewPasswordMonitoring(false);
      setShowAddForm(false);
      await loadData();
    } catch (error) {
      console.error('Error adding password:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePassword = async (passwordId: number) => {
    setSaving(true);
    try {
      await api.put(`/clients/${clientId}/password-management/passwords/${passwordId}`, {
        label: editingData.label,
        username: editingData.username,
        password: editingData.password,
        note_verification: editingData.note,
        email_verification: editingData.email,
        phone_verification: editingData.phone,
        use_for_monitoring: editingData.monitoring,
      });
      setEditingPasswordId(null);
      setEditingData({ label: '', username: '', password: '', note: '', email: '', phone: '', monitoring: false });
      await loadData();
    } catch (error) {
      console.error('Error updating password:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePassword = async (passwordId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet identifiant ?')) {
      return;
    }

    setSaving(true);
    try {
      await api.delete(`/clients/${clientId}/password-management/passwords/${passwordId}`);
      await loadData();
    } catch (error) {
      console.error('Error deleting password:', error);
    } finally {
      setSaving(false);
    }
  };

  const startEditingPassword = (pwd: any) => {
    setEditingPasswordId(pwd.id);
    setExpandedRow(pwd.id);
    setEditingData({
      label: pwd.label,
      username: pwd.username || '',
      password: pwd.password,
      note: pwd.note_verification || '',
      email: pwd.email_verification || '',
      phone: pwd.phone_verification || '',
      monitoring: pwd.use_for_monitoring || false,
    });
  };

  const cancelEditing = () => {
    setEditingPasswordId(null);
    setEditingData({ label: '', username: '', password: '', note: '', email: '', phone: '', monitoring: false });
  };

  const handleResetPassword = async () => {
    if (!window.confirm('Générer un nouveau mot de passe temporaire pour ce client ?')) {
      return;
    }

    setSaving(true);
    try {
      const response = await api.post(`/clients/${clientId}/password-management/reset`);
      alert(`Nouveau mot de passe: ${response.data.data.temp_password}`);
      await loadData();
    } catch (error) {
      console.error('Error resetting password:', error);
    } finally {
      setSaving(false);
    }
  };

  const togglePasswordVisibility = (key: string) => {
    setShowPassword(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copié dans le presse-papier');
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setEditingPasswordId(null);
    setShowAddForm(false);
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Typography>Chargement...</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Gestion des mots de passe
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {clientName}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Mots de passe" />
        <Tab label="Historique" />
      </Tabs>

      <DialogContent dividers sx={{ minHeight: 400 }}>
        {/* Tab 1: Mots de passe */}
        {activeTab === 0 && data && (
          <Box>
            {/* En-tête avec bouton Ajouter */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Identifiants enregistrés
              </Typography>
              {!showAddForm && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setShowAddForm(true)}
                  size="small"
                >
                  Ajouter un identifiant
                </Button>
              )}
            </Box>

            {/* Formulaire d'ajout */}
            {showAddForm && (
              <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Nouvel identifiant
                  </Typography>
                  <IconButton size="small" onClick={() => setShowAddForm(false)}>
                    <CloseIcon />
                  </IconButton>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      size="small"
                      label="Label"
                      value={newPasswordLabel}
                      onChange={(e) => setNewPasswordLabel(e.target.value)}
                      fullWidth
                      required
                      placeholder="Ex: Email, Banque, Compte Google..."
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      size="small"
                      label="Identifiant"
                      value={newPasswordUsername}
                      onChange={(e) => setNewPasswordUsername(e.target.value)}
                      fullWidth
                      placeholder="Nom d'utilisateur, email..."
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      size="small"
                      label="Mot de passe"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      fullWidth
                      placeholder="Mot de passe du compte"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      size="small"
                      label="Email de vérification"
                      value={newPasswordEmail}
                      onChange={(e) => setNewPasswordEmail(e.target.value)}
                      fullWidth
                      placeholder="Email associé à ce compte"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      size="small"
                      label="Téléphone de vérification"
                      value={newPasswordPhone}
                      onChange={(e) => setNewPasswordPhone(e.target.value)}
                      fullWidth
                      placeholder="Téléphone associé à ce compte"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      size="small"
                      label="Notes"
                      value={newPasswordNote}
                      onChange={(e) => setNewPasswordNote(e.target.value)}
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Questions secrètes, informations supplémentaires..."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newPasswordMonitoring}
                          onChange={(e) => setNewPasswordMonitoring(e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MonitorIcon fontSize="small" />
                          <Typography variant="body2">Utiliser pour le monitoring</Typography>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleAddPassword}
                        disabled={!newPasswordLabel || saving}
                      >
                        Enregistrer
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setShowAddForm(false);
                          setNewPasswordLabel('');
                          setNewPasswordUsername('');
                          setNewPassword('');
                          setNewPasswordNote('');
                          setNewPasswordEmail('');
                          setNewPasswordPhone('');
                          setNewPasswordMonitoring(false);
                        }}
                      >
                        Annuler
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* Tableau unifié de tous les mots de passe */}
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell width={40} sx={{ py: 1 }}></TableCell>
                    <TableCell sx={{ py: 1 }}>Label</TableCell>
                    <TableCell sx={{ py: 1 }}>Identifiant / Mot de passe</TableCell>
                    <TableCell align="center" width={100} sx={{ py: 1 }}>
                      <Tooltip title="Monitoring">
                        <MonitorIcon fontSize="small" />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" width={120} sx={{ py: 1 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Espace client si activé */}
                  {data.username && (
                    <React.Fragment>
                      <TableRow hover sx={{ bgcolor: 'primary.50' }}>
                        <TableCell sx={{ py: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => setExpandedRow(expandedRow === -1 ? null : -1)}
                          >
                            {expandedRow === -1 ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                            Espace Client
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Compte principal
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                {data.username}
                              </Typography>
                              <IconButton size="small" onClick={() => copyToClipboard(data.username)} sx={{ p: 0.5 }}>
                                <ContentCopyIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                            {data.temp_password && !data.password_changed && (
                              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                  {showPassword['main'] ? data.temp_password : '••••••••••••••••'}
                                </Typography>
                                <IconButton size="small" onClick={() => togglePasswordVisibility('main')} sx={{ p: 0.5 }}>
                                  {showPassword['main'] ? <VisibilityOffIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                                </IconButton>
                                <IconButton size="small" onClick={() => copyToClipboard(data.temp_password!)} sx={{ p: 0.5 }}>
                                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1 }}>
                          <Chip
                            label={data.is_active ? 'Actif' : 'Inactif'}
                            color={data.is_active ? 'success' : 'error'}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleResetPassword}
                            disabled={saving}
                            sx={{ py: 0.25, px: 1, fontSize: '0.75rem' }}
                          >
                            Réinit.
                          </Button>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                          <Collapse in={expandedRow === -1} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 3, bgcolor: 'grey.50' }}>
                              <Grid container spacing={2}>
                                {data.password_changed && (
                                  <Grid item xs={12}>
                                    <Chip label="Mot de passe changé" color="success" size="small" />
                                  </Grid>
                                )}
                                {!data.password_changed && data.temp_password && (
                                  <Grid item xs={12}>
                                    <Alert severity="warning">
                                      Mot de passe temporaire actif - Le client doit changer son mot de passe à la première connexion
                                    </Alert>
                                  </Grid>
                                )}
                                {data.last_login && (
                                  <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">
                                      Dernière connexion: {new Date(data.last_login).toLocaleString('fr-FR')}
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  )}

                  {/* Liste des autres mots de passe */}
                  {data.passwords && data.passwords.length > 0 ? (
                    data.passwords.map((pwd) => {
                      const isExpanded = expandedRow === pwd.id;
                      const isEditing = editingPasswordId === pwd.id;

                      return (
                        <React.Fragment key={pwd.id}>
                          <TableRow hover>
                            <TableCell sx={{ py: 1 }}>
                              <IconButton size="small" onClick={() => setExpandedRow(isExpanded ? null : pwd.id)} sx={{ p: 0.5 }}>
                                {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                {pwd.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                {new Date(pwd.created_at).toLocaleDateString('fr-FR')}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {pwd.username && (
                                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                      {pwd.username}
                                    </Typography>
                                    <IconButton size="small" onClick={() => copyToClipboard(pwd.username || '')} sx={{ p: 0.5 }}>
                                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Box>
                                )}
                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                    {showPassword[`pwd-${pwd.id}`] ? pwd.password : '••••••••••••'}
                                  </Typography>
                                  <IconButton size="small" onClick={() => togglePasswordVisibility(`pwd-${pwd.id}`)} sx={{ p: 0.5 }}>
                                    {showPassword[`pwd-${pwd.id}`] ? <VisibilityOffIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                                  </IconButton>
                                  <IconButton size="small" onClick={() => copyToClipboard(pwd.password)} sx={{ p: 0.5 }}>
                                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 1 }}>
                              <Checkbox
                                checked={pwd.use_for_monitoring}
                                disabled
                                color="primary"
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ py: 1 }}>
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => startEditingPassword(pwd)}
                                  color="primary"
                                  sx={{ p: 0.5 }}
                                >
                                  <EditIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeletePassword(pwd.id)}
                                  color="error"
                                  sx={{ p: 0.5 }}
                                >
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 3, bgcolor: 'grey.50' }}>
                                  {isEditing ? (
                                    <Box>
                                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                                        Modifier l'identifiant
                                      </Typography>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                          <TextField
                                            size="small"
                                            label="Label"
                                            value={editingData.label}
                                            onChange={(e) => setEditingData({ ...editingData, label: e.target.value })}
                                            fullWidth
                                          />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                          <TextField
                                            size="small"
                                            label="Identifiant"
                                            value={editingData.username}
                                            onChange={(e) => setEditingData({ ...editingData, username: e.target.value })}
                                            fullWidth
                                          />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                          <TextField
                                            size="small"
                                            label="Mot de passe"
                                            value={editingData.password}
                                            onChange={(e) => setEditingData({ ...editingData, password: e.target.value })}
                                            fullWidth
                                          />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                          <TextField
                                            size="small"
                                            label="Email de vérification"
                                            value={editingData.email}
                                            onChange={(e) => setEditingData({ ...editingData, email: e.target.value })}
                                            fullWidth
                                          />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                          <TextField
                                            size="small"
                                            label="Téléphone de vérification"
                                            value={editingData.phone}
                                            onChange={(e) => setEditingData({ ...editingData, phone: e.target.value })}
                                            fullWidth
                                          />
                                        </Grid>
                                        <Grid item xs={12}>
                                          <TextField
                                            size="small"
                                            label="Notes"
                                            value={editingData.note}
                                            onChange={(e) => setEditingData({ ...editingData, note: e.target.value })}
                                            fullWidth
                                            multiline
                                            rows={2}
                                          />
                                        </Grid>
                                        <Grid item xs={12}>
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                checked={editingData.monitoring}
                                                onChange={(e) => setEditingData({ ...editingData, monitoring: e.target.checked })}
                                              />
                                            }
                                            label={
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <MonitorIcon fontSize="small" />
                                                <Typography variant="body2">Utiliser pour le monitoring</Typography>
                                              </Box>
                                            }
                                          />
                                        </Grid>
                                        <Grid item xs={12}>
                                          <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button
                                              variant="contained"
                                              size="small"
                                              onClick={() => handleUpdatePassword(pwd.id)}
                                              disabled={saving}
                                            >
                                              Enregistrer
                                            </Button>
                                            <Button
                                              variant="outlined"
                                              size="small"
                                              onClick={cancelEditing}
                                            >
                                              Annuler
                                            </Button>
                                          </Box>
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  ) : (
                                    <Box>
                                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                                        Informations de vérification
                                      </Typography>
                                      <Grid container spacing={2}>
                                        {pwd.email_verification && (
                                          <Grid item xs={12} md={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <EmailIcon fontSize="small" color="action" />
                                              <Box>
                                                <Typography variant="caption" color="text.secondary">Email</Typography>
                                                <Typography variant="body2">{pwd.email_verification}</Typography>
                                              </Box>
                                            </Box>
                                          </Grid>
                                        )}
                                        {pwd.phone_verification && (
                                          <Grid item xs={12} md={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <PhoneIcon fontSize="small" color="action" />
                                              <Box>
                                                <Typography variant="caption" color="text.secondary">Téléphone</Typography>
                                                <Typography variant="body2">{pwd.phone_verification}</Typography>
                                              </Box>
                                            </Box>
                                          </Grid>
                                        )}
                                        {pwd.note_verification && (
                                          <Grid item xs={12}>
                                            <Typography variant="caption" color="text.secondary">Notes</Typography>
                                            <Typography variant="body2">{pwd.note_verification}</Typography>
                                          </Grid>
                                        )}
                                        {!pwd.email_verification && !pwd.phone_verification && !pwd.note_verification && (
                                          <Grid item xs={12}>
                                            <Typography variant="body2" color="text.secondary">
                                              Aucune information de vérification
                                            </Typography>
                                          </Grid>
                                        )}
                                      </Grid>
                                    </Box>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  ) : (
                    !data.username && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              Aucun identifiant enregistré
                            </Typography>
                            {!showAddForm && (
                              <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => setShowAddForm(true)}
                                sx={{ mt: 2 }}
                              >
                                Ajouter un identifiant
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        )}

        {/* Tab 2: Historique */}
        {activeTab === 1 && data && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Historique des changements
            </Typography>

            {data.history && data.history.length > 0 ? (
              <List>
                {data.history.map((entry) => (
                  <ListItem key={entry.id} divider sx={{ display: 'block', py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <HistoryIcon sx={{ mr: 2, mt: 0.5, color: 'text.secondary' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {entry.action_details === 'Suppression' ? (
                            `Suppression - ${entry.password_label || 'Mot de passe'}`
                          ) : entry.change_type === 'reset' ? (
                            `Réinitialisation - ${entry.password_label || 'Espace Client'}`
                          ) : (
                            `Mise à jour - ${entry.password_label || 'Mot de passe'}`
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {new Date(entry.changed_at).toLocaleString('fr-FR')}
                          {' • '}
                          Par {entry.changed_by}
                        </Typography>

                        {/* Affichage des anciennes valeurs */}
                        {(entry.old_username || entry.old_password) && (
                          <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 1 }}>
                              Anciennes valeurs :
                            </Typography>
                            {entry.old_username && (
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Identifiant :
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                    {entry.old_username}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => copyToClipboard(entry.old_username!)}
                                    sx={{ p: 0.25 }}
                                  >
                                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Box>
                              </Box>
                            )}
                            {entry.old_password && (
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Mot de passe :
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                    {showPassword[`history-${entry.id}`] ? entry.old_password : '••••••••••••'}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => togglePasswordVisibility(`history-${entry.id}`)}
                                    sx={{ p: 0.25 }}
                                  >
                                    {showPassword[`history-${entry.id}`] ? (
                                      <VisibilityOffIcon sx={{ fontSize: 14 }} />
                                    ) : (
                                      <VisibilityIcon sx={{ fontSize: 14 }} />
                                    )}
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => copyToClipboard(entry.old_password!)}
                                    sx={{ p: 0.25 }}
                                  >
                                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Box>
                              </Box>
                            )}
                          </Paper>
                        )}
                      </Box>
                    </Box>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Aucun historique disponible
                </Typography>
              </Paper>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordManagementModal;
